import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { t, font, Button, Panel } from "@realtyflows/ui";
import { sendVerificationCode, verifyVerificationCode, verifyEmailToken, checkVerification, signup, saveToken } from "../api";
import useResponsive from "../hooks/useResponsive";
import useScrollToAlert from "../hooks/useScrollToError";

export default function VerifyEmail({ pendingForm: pendingFormProp, onVerified }) {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const [searchParams] = useSearchParams();
  const [cooldown, setCooldown] = useState(60);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const errorRef = useScrollToAlert(error);
  const [verified, setVerified] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef([]);

  const tokenFromUrl = searchParams.get("token");
  const emailFromUrl = searchParams.get("email");

  // Get pendingForm from prop OR localStorage (for verify link in new tab)
  const pendingForm = pendingFormProp || (() => {
    try {
      const stored = localStorage.getItem("rf_pending_signup");
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  })();

  const email = pendingForm?.email || emailFromUrl || "";

  // If user clicked the verification link (token in URL), verify and close tab
  useEffect(() => {
    if (!tokenFromUrl || !emailFromUrl) return;
    let cancelled = false;

    const verifyLink = async () => {
      try {
        await verifyEmailToken(tokenFromUrl, emailFromUrl);
        if (!cancelled) {
          setVerified(true);
          // Close this tab after 3 seconds — original tab handles signup via polling
          setTimeout(() => { window.close(); }, 3000);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Verification link is invalid or expired");
      }
    };
    verifyLink();

    return () => { cancelled = true; };
  }, [tokenFromUrl, emailFromUrl]);

  // Poll every 3 seconds to check if email was verified (user clicked link in another tab)
  useEffect(() => {
    if (!email || verified || tokenFromUrl) return;
    let cancelled = false;

    const poll = setInterval(async () => {
      try {
        const { verified: v } = await checkVerification(email);
        if (v && !cancelled) setVerified(true);
      } catch {}
    }, 3000);

    return () => { cancelled = true; clearInterval(poll); };
  }, [email, verified, tokenFromUrl]);

  // Once verified, create backend account and auto-login
  const completeSignup = useCallback(async () => {
    if (!pendingForm) return;
    try {
      const { token, user } = await signup(pendingForm);
      saveToken(token);
      try { localStorage.removeItem("rf_pending_signup"); } catch {}
      onVerified(user);
    } catch (err) {
      setError(err.message);
    }
  }, [pendingForm, onVerified]);

  useEffect(() => {
    if (verified) completeSignup();
  }, [verified, completeSignup]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    setResending(true);
    setError("");
    try {
      await sendVerificationCode({ email, purpose: "signup" });
      setCooldown(60);
    } catch (err) {
      setError(err.message || "Failed to resend");
    } finally {
      setResending(false);
    }
  };

  // Code input handlers
  const handleCodeInput = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError("");
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
    if (digit && index === 5 && newCode.every(d => d)) handleVerifyCode(newCode.join(""));
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted.length) return;
    const newCode = [...code];
    for (let i = 0; i < 6; i++) newCode[i] = pasted[i] || "";
    setCode(newCode);
    const next = newCode.findIndex(d => !d);
    inputRefs.current[next >= 0 ? next : 5]?.focus();
    if (pasted.length === 6) handleVerifyCode(pasted);
  };

  const handleVerifyCode = async (codeStr) => {
    setVerifying(true);
    setError("");
    try {
      await verifyVerificationCode({ email, code: codeStr, purpose: "signup" });
      setVerified(true);
    } catch (err) {
      setError(err.message || "Invalid code");
      setVerifying(false);
    }
  };

  // No pending form and no token — nothing to verify
  if (!pendingForm && !tokenFromUrl) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: t.pageBg, padding: isMobile ? "24px 16px" : 0 }}>
        <Panel padding={isMobile ? 20 : 32} style={{ width: isMobile ? "100%" : 420, maxWidth: 420, textAlign: "center" }}>
          <div style={{ fontSize: 14, color: t.textMuted, fontFamily: font.body, marginBottom: 16 }}>
            No pending verification found.
          </div>
          <Button variant="primary" fullWidth={isMobile} onClick={() => navigate("/signup")}>Back to Sign Up</Button>
        </Panel>
      </div>
    );
  }

  // Verify link tab — verified, show success + closing message
  if (verified && tokenFromUrl) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: t.pageBg, padding: isMobile ? "24px 16px" : 0 }}>
        <div className="rf-a1" style={{ width: isMobile ? "100%" : 440, maxWidth: 440 }}>
          <Panel padding={isMobile ? 20 : 32}>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: isMobile ? 52 : 64, height: isMobile ? 52 : 64, borderRadius: isMobile ? 12 : 16, margin: "0 auto 20px", background: t.emeraldLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMobile ? 24 : 28 }}>&#10003;</div>
              <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>Email Verified!</div>
              <div style={{ fontSize: isMobile ? 12 : 13, color: t.textSecondary, fontFamily: font.body, marginBottom: 12 }}>
                This tab will close automatically...
              </div>
              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                Return to the signup tab to continue.
              </div>
            </div>
          </Panel>
        </div>
      </div>
    );
  }

  const codeInputSize = isMobile ? 38 : 44;
  const codeInputHeight = isMobile ? 46 : 52;

  const codeInputStyle = {
    width: codeInputSize, height: codeInputHeight, textAlign: "center",
    fontSize: isMobile ? 18 : 22, fontWeight: 700, fontFamily: font.mono,
    background: t.surfaceWarm, border: `2px solid ${t.border}`,
    borderRadius: isMobile ? 8 : 10, outline: "none", color: t.textPrimary,
    transition: "border-color 0.2s",
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: t.pageBg, padding: isMobile ? "24px 16px" : 0 }}>
      <div className="rf-a1" style={{ width: isMobile ? "100%" : 440, maxWidth: 440 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: isMobile ? 20 : 32 }}>
          <div onClick={() => navigate("/")} style={{ display: "inline-flex", alignItems: "center", gap: isMobile ? 8 : 10, marginBottom: 8, cursor: "pointer" }}>
            <img src="/logo.png" alt="RealtyFlows" style={{ width: isMobile ? 24 : 28, height: isMobile ? 24 : 28, borderRadius: 4 }} />
            <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              Realty<span style={{ color: t.gold }}>Flows</span>
            </span>
          </div>
        </div>

        <Panel padding={isMobile ? 20 : 32}>
          <div style={{ textAlign: "center" }}>
            {/* Email icon */}
            <div style={{
              width: isMobile ? 52 : 64, height: isMobile ? 52 : 64, borderRadius: isMobile ? 12 : 16, margin: "0 auto 20px",
              background: t.emeraldLight, display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: isMobile ? 24 : 28,
            }}>
              &#9993;
            </div>

            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: t.textPrimary, marginBottom: 8, fontFamily: font.display }}>
              {verified ? "Email Verified!" : "Verify your email"}
            </div>

            {verified ? (
              <div style={{ fontSize: isMobile ? 12 : 13, color: t.emerald, fontFamily: font.body, marginBottom: 20 }}>
                Setting up your account...
              </div>
            ) : (
              <>
                <div style={{ fontSize: isMobile ? 12 : 13, color: t.textSecondary, fontFamily: font.body, lineHeight: 1.6, marginBottom: isMobile ? 20 : 24 }}>
                  We sent a verification email to<br />
                  <span style={{ fontWeight: 600, color: t.textPrimary, wordBreak: "break-all" }}>{email}</span><br />
                  Click the link in the email to verify your account.
                </div>

                {error && (
                  <div ref={errorRef} style={{
                    background: t.coralLight, border: `1px solid ${t.coral}40`, borderRadius: 8,
                    padding: isMobile ? "8px 12px" : "10px 14px", marginBottom: 16, fontSize: 12, color: t.coral, fontFamily: font.body,
                  }}>
                    {error}
                  </div>
                )}

                {/* Waiting indicator */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  padding: isMobile ? "10px 12px" : "12px 16px", background: t.surfaceWarm, borderRadius: 10, marginBottom: isMobile ? 16 : 20,
                  border: `1px solid ${t.border}`,
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%", background: t.emerald,
                    animation: "pulse 2s infinite",
                  }} />
                  <span style={{ fontSize: isMobile ? 11 : 12, color: t.textSecondary, fontFamily: font.body }}>
                    Waiting for verification...
                  </span>
                </div>

                {/* Manual code entry toggle */}
                {!showCodeInput ? (
                  <div style={{ marginBottom: 16 }}>
                    <span
                      onClick={() => { setShowCodeInput(true); setTimeout(() => inputRefs.current[0]?.focus(), 50); }}
                      style={{ fontSize: 12, color: t.emerald, cursor: "pointer", fontWeight: 600, fontFamily: font.body }}
                    >
                      Enter code manually instead
                    </span>
                  </div>
                ) : (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, marginBottom: 10 }}>
                      Enter the 6-digit code from the email:
                    </div>
                    <div style={{ display: "flex", gap: isMobile ? 4 : 6, justifyContent: "center", marginBottom: 12 }} onPaste={handleCodePaste}>
                      {code.map((digit, i) => (
                        <input
                          key={i}
                          ref={el => inputRefs.current[i] = el}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={e => handleCodeInput(i, e.target.value)}
                          onKeyDown={e => handleCodeKeyDown(i, e)}
                          onFocus={e => e.target.select()}
                          disabled={verifying}
                          style={{ ...codeInputStyle, borderColor: digit ? t.emerald : error ? t.coral : t.border }}
                        />
                      ))}
                    </div>
                    <Button variant="primary" fullWidth size="sm" onClick={() => handleVerifyCode(code.join(""))} disabled={verifying || code.some(d => !d)}>
                      {verifying ? "Verifying..." : "Verify Code"}
                    </Button>
                  </div>
                )}

                {/* Resend button */}
                <div style={{ marginBottom: 16 }}>
                  {cooldown > 0 ? (
                    <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
                      Resend available in {cooldown}s
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" fullWidth={isMobile} onClick={handleResend} disabled={resending}>
                      {resending ? "Sending..." : "Resend Verification Email"}
                    </Button>
                  )}
                </div>

                {/* Check spam note */}
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, lineHeight: 1.5 }}>
                  Don't see the email? Check your spam folder.
                </div>
              </>
            )}
          </div>
        </Panel>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <span onClick={() => navigate("/signup")} style={{ fontSize: 12, color: t.textMuted, cursor: "pointer", fontFamily: font.body }}>
            &larr; Back to sign up
          </span>
        </div>
      </div>
    </div>
  );
}
